import { Typography } from '@mui/material';
import Button from 'components/Button';
import ErrorBoundary from 'components/ErrorBoundary';
import PageLoader from 'components/PageLoader';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import { Column, Row } from 'further-ui/components/Layout';
import StaticField from 'further-ui/components/StaticField';
import { useDisclosure } from 'further-ui/hooks';
import { CalendarDay, dateToLabel } from 'further-ui/utils';
import { revaluation as rules } from 'further-ui/validations';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import { compact, groupBy, sortBy } from 'lodash';
import { useFormContext } from 'react-hook-form';
import processRevaluationHistory, {
  ProcessedRevaluation,
} from '../../helpers/processRevaluationHistory';
import useRevaluations from '../hooks/useRevaluations';
import RevaluationHistoryTable from '../RevaluationHistoryTable';
import HoldingClassTransactionAllocator from './HoldingClassTransactionAllocator';

type ReviewRevaluationProps = {
  handlePreviousStep?: () => void;
  handleSubmit?: () => void;
  vehicle: {
    _id: string;
    vehicleName: string;
    baseCurrency: CurrencyCode;
    fxDataFeed: FxDataFeed;
  };
  assetName: string;
  assetId: string;
  revaluationDate?: CalendarDay;
};

function getLatestRevaluationsForEachHoldingClass(
  revaluationHistory: Array<ProcessedRevaluation>,
): Array<ProcessedRevaluation> {
  const groupedByHoldingClass = groupBy(
    revaluationHistory,
    (rev) => rev.holdingClassId,
  );

  const latestRevaluations = Object.values(groupedByHoldingClass).map(
    (classRevaluations) => {
      const sortedRevaluations = sortBy(classRevaluations, 'ledgerDate');
      const latestRevaluation =
        sortedRevaluations[sortedRevaluations.length - 1];

      return latestRevaluation;
    },
  );

  return compact(latestRevaluations);
}

const ReviewRevaluation: React.FC<ReviewRevaluationProps> = ({
  handlePreviousStep,
  handleSubmit,
  vehicle,
  assetName,
  assetId,
  revaluationDate: propRevaluationDate,
}) => {
  const notification = useNotification();
  const { getValues, watch, control } =
    useFormContext<rules.RevaluationFormType>();

  const { hasEqualisationInDraftState } = useEqualisations(vehicle._id);
  const confirmDialog = useDisclosure();

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const formRevaluationDate = getValues('currentRevaluationDate');
  const revaluationDate =
    propRevaluationDate?.toUTCDate() ?? formRevaluationDate;

  const { getAllRevaluations } = useRevaluations({
    watch,
    control,
    getValues,
  });

  const mutationOptions = useHandleTrpcMutation()({
    onSuccess: () => {
      handleSubmit?.();
    },
  });

  const { mutate: createRevaluations, isPending: isCreatingRevaluations } =
    api.lpRevaluation.create.useMutation(mutationOptions);

  const allRevaluations = getAllRevaluations().filter((rev) => !rev.isRemoved);
  const revaluationHistory = processRevaluationHistory(allRevaluations);

  const latestRevaluationsWithChanges =
    getLatestRevaluationsForEachHoldingClass(revaluationHistory).filter(
      (rev) => rev.isNew && rev.hasChange,
    );

  const currency = allRevaluations.pop()?.currency;

  if (!currency) {
    throw new Error("Can't find revaluation currency.");
  }

  const onSubmit = (data: rules.RevaluationFormType) => {
    try {
      const formData = {
        revaluations: latestRevaluationsWithChanges.map((rev) => ({
          ...rev,
          currentNumberOfUnits: rev.currentNumberOfUnits,
          ledgerDate: rev.date,
          unitPriceTcy: rev.newUnitPrice,
          currency: rev.currency,
          assetId,
        })),
        revaluationTransactions: data.revaluationTransactions,
        revaluationCurrencyRates: data.revaluationCurrencyRates ?? {},
        assetId,
        vehicleId: vehicle._id,
      };

      createRevaluations(formData);
    } catch {
      notification.error('Something went wrong.');
    }
  };

  const hasChanges = latestRevaluationsWithChanges.some((rev) => rev.hasChange);

  if (transactionTypesLoading) {
    return <PageLoader />;
  }

  if (!transactionTypeOptions.length) {
    return (
      <Typography>
        To set up account mappings, please first create some transaction types.
      </Typography>
    );
  }

  return (
    <ErrorBoundary>
      <Column spacing="lg">
        <Typography variant="h5">Review revaluation</Typography>
        <StaticField label="Vehicle">{vehicle.vehicleName}</StaticField>
        <StaticField label="Asset">{assetName}</StaticField>
        <StaticField label="Revaluation date">
          {dateToLabel(revaluationDate)}
        </StaticField>

        <RevaluationHistoryTable
          revaluations={latestRevaluationsWithChanges}
          removeColumns={['date', 'function', 'type']}
        />
        {latestRevaluationsWithChanges.map((rev) => (
          <ErrorBoundary key={rev.holdingClassId}>
            <HoldingClassTransactionAllocator
              assetId={assetId}
              revaluation={rev}
              transactionTypeOptions={transactionTypeOptions ?? []}
              vehicle={vehicle}
            />
          </ErrorBoundary>
        ))}

        {handlePreviousStep && handleSubmit && (
          <Row centered spacing="sm" justify="space-between">
            <Button
              variant="outlined"
              color="primary"
              onClick={handlePreviousStep}
              size="large"
            >
              Previous step
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={
                hasEqualisationInDraftState
                  ? confirmDialog.onOpen
                  : () => onSubmit(getValues())
              }
              loading={isCreatingRevaluations}
              disabled={!hasChanges}
              size="large"
            >
              Save and approve
            </Button>
            {hasEqualisationInDraftState && (
              <PendingDraftEqualisationsModal
                isOpen={confirmDialog.isOpen}
                onClose={confirmDialog.onClose}
                onConfirm={() => onSubmit(getValues())}
                loading={isCreatingRevaluations}
              />
            )}
          </Row>
        )}
      </Column>
    </ErrorBoundary>
  );
};

export default ReviewRevaluation;
